import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ReferenceBasketVersion } from '~/Api/BgPriceReferencing/MaxPrice';
import { Country } from '~/Api/Country';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { BgMaxPrice as ApiBgMaxPrice, ErrorResponse, Product, ProductUpdate } from '~/Api/User/ProductGroup/BgMaxPrice';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
	State as PriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneAllPrices } from '~/ProductPrice';
import { ThunkDispatch } from '~/Store';

import { Action } from './Action';
import {
	commitBgMaxPriceUpdate,
	fetchBgMaxPrice,
	handleBgMaxPriceUpdateResponse,
	handleCommitError,
	pruneBgMaxPrice,
	scheduleBgMaxPriceUpdate,
} from './actions';
import BgMaxPriceContent from './BgMaxPrice';
import { selectBgMaxPrice, selectExport, State as BgMaxPriceState } from './reducer';

interface BasketProduct {
	country: Country;
	product: Product;
	usedForCalculation: boolean;
}
interface BasketOptionalProduct {
	country: Country;
	product: Product | null;
	usedForCalculation: boolean;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne((bgMaxPrice: ApiBgMaxPrice | null, versions: ReferenceBasketVersion[] | null) => {
	const version =
		versions !== null && bgMaxPrice !== null
			? versions.find((version) => version.id === bgMaxPrice.reference.referenceBasketVersion) ?? null
			: null;
	const referenceCountries = version !== null ? version.referenceCountries : [];
	const products = bgMaxPrice !== null && version !== null ? bgMaxPrice.products : [];
	const nonsetCountries = new Set(referenceCountries?.map((country) => country.alpha2));
	const usedForCalculationIds = new Set(
		bgMaxPrice?.finalCalculation?.ceilingPrice?.fromProducts?.map((product) => product.id)
	);

	const bulgarianProducts: BasketOptionalProduct[] = [];
	const referencedProducts: BasketOptionalProduct[] = [];
	const otherProducts: BasketProduct[] = [];

	products.forEach((product) => {
		const { country } = product.product.dataset.source;
		if (country.alpha2 === 'BG') {
			bulgarianProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			return;
		}
		if (containsCountry(referenceCountries ?? [], country.alpha2)) {
			referencedProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			nonsetCountries.delete(country.alpha2);
			return;
		}
		otherProducts.push({
			country,
			product,
			usedForCalculation: usedForCalculationIds.has(product.product.id),
		});
	});

	if (bulgarianProducts.length === 0) {
		bulgarianProducts.push({
			country: new Country('BG', 'Bulgaria'),
			product: null,
			usedForCalculation: false,
		});
	}

	referenceCountries?.forEach((country) => {
		if (nonsetCountries.has(country.alpha2)) {
			referencedProducts.push({
				country,
				product: null,
				usedForCalculation: false,
			});
		}
	});

	bulgarianProducts.sort(compareReferences);
	referencedProducts.sort(compareReferences);
	otherProducts.sort(compareReferences);

	const basketProducts = [...bulgarianProducts, ...referencedProducts, ...otherProducts].filter(
		(product) => product.product !== null
	) as BasketProduct[];
	const pricedProducts = basketProducts.map(
		(product) => new PricedProduct(product.product, product.product.product, null)
	);

	return {
		bulgarianProducts,
		otherProducts,
		pricedProducts,
		referenceBasketVersion: version,
		referencedProducts,
	};
});

interface State {
	bgMaxPrice: BgMaxPriceState;
	priceReferencing: PriceReferencingState;
}

const mapStateToProps = (state: State, { group }: { group: UserProductGroup }) => {
	const bgMaxPriceState = selectBgMaxPrice(state.bgMaxPrice, group.id);
	const bgMaxPrice = bgMaxPriceState?.bgMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.bgMaxPrice
	);

	return {
		...groupProducts(bgMaxPrice, versions),
		bgMaxPrice,
		isFetching: Boolean(bgMaxPriceState?.isFetching),
		validationResult: selectExport(state.bgMaxPrice, group.id)?.validationResult ?? null,
		versions,
	};
};

const actions = {
	onFetch: (group: UserProductGroup) => async (dispatch: ThunkDispatch<Action>) => {
		dispatch(fetchReferenceBasketVersions(Module.bgMaxPrice));

		try {
			await dispatch(commitBgMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}

		try {
			await dispatch(fetchBgMaxPrice(group.id));
		} catch (error) {
			if (!(error instanceof ErrorResponse)) {
				dispatch(handleError(error));
				return;
			}
			error.handleProductGroupNotFound();
			dispatch(handleError(error));
		}
	},
	onPrune: (group: UserProductGroup) => async (dispatch: ThunkDispatch<AnyAction>) => {
		try {
			await dispatch(commitBgMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleCommitError(error));
		}
		dispatch(pruneBgMaxPrice(group.id));
		dispatch(pruneReferenceBasketVersions());
		dispatch(pruneAllPrices());
		dispatch(pruneDatasets());
	},
	onSave: (group: UserProductGroup) => async (dispatch: ThunkDispatch<Action>) => {
		try {
			await dispatch(commitBgMaxPriceUpdate(group.id));
		} catch (error) {
			dispatch(handleBgMaxPriceUpdateResponse(error, group.id));
		}
	},
	onUpdate:
		(
			group: UserProductGroup,
			basketVersion: string,
			packageSize: number | null,
			historicProductDataViewDate: Date | null,
			products: ProductUpdate[]
		) =>
		async (dispatch: ThunkDispatch<Action>) => {
			try {
				await dispatch(
					scheduleBgMaxPriceUpdate(group.id, basketVersion, packageSize, historicProductDataViewDate, products)
				);
			} catch (error) {
				dispatch(handleBgMaxPriceUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<Action>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(BgMaxPriceContent);
